/**
 * Housing Unit Card Selected Epic
 *
 * Reacts to housingUnitCardSelected actions and dispatches navigateTo to switch to housing
 * unit detail with resolved lot context preserved. Every housing unit card (including a Similar
 * Home one under the Sold chip, NHS20-8827) opens the same chip experience its own lot status
 * belongs to — Model homes the Model chip, Quick Move-Ins Available — same as clicking it from
 * its own chip's listing, falling back to Available when the lot can't be resolved (matching
 * this epic's pre-existing selectedLotId fallback below). When that chip isn't already active,
 * this first dispatches chipSelected (its existing reducer resets to a fresh listing for it)
 * before navigateTo, which then pushes that fresh state as the view-history entry Back returns
 * to, instead of the Sold card the user actually clicked from.
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {Observable} params.store$ - Redux store state observable
 * @returns {import('redux-observable').Epic}
 */

import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ofType } from 'redux-observable';
import { VIEW_HOUSING_UNIT_DETAIL, TAB_QUICK_MOVE_INS } from '../constants';
import { selectSelectedLotId, selectHousingUnits, selectLotsById, selectActiveChip } from './selectors';
import LotStatus from '../enums/lotStatus';

function createHousingUnitCardSelectedEpic({ dataActions, store$ }) {
  return (action$) => action$.pipe(
    ofType(dataActions.housingUnitCardSelected.type),
    withLatestFrom(store$),
    mergeMap(([action, state]) => {
      const activeChip = selectActiveChip(state);
      // A Similar Home click only ever happens from the Sold chip's own listing (Sold cards
      // themselves aren't clickable — see components/soldHomeCard.dom.js) and always targets an
      // unrelated home, so the previously selected sold lot must not carry over into the new
      // detail view (NHS20-8827 selectedLotId leak).
      const isSimilarHomeClick = activeChip === LotStatus.Sold.toString();
      const selectedLotId = isSimilarHomeClick ? null : (selectSelectedLotId(state) ?? null);
      const housingUnits = selectHousingUnits(state);
      const lotsById = selectLotsById(state);
      const housingUnitId = Number(action.payload);
      const housingUnit = housingUnits.find((unit) => (
        unit.specificationId === housingUnitId || unit.housingUnitId === housingUnitId
      )) ?? null;
      const lot = housingUnit ? lotsById[housingUnit.lotId] ?? null : null;
      const targetChip = lot?.status ?? LotStatus.Available.toString();

      const navigateToAction = dataActions.navigateTo({
        view: VIEW_HOUSING_UNIT_DETAIL,
        context: {
          planId: null,
          selectedLotId: lot?.sitePlanLotId ?? selectedLotId,
          housingUnitId: housingUnit?.specificationId ?? housingUnitId,
        },
      });

      if (activeChip === targetChip) {
        return of(navigateToAction);
      }

      // chipSelected always resets selectedTab to Floor Plans for the Available chip (the
      // Floor-Plans-first default per NHS20-8054) — a Quick Move-In housing unit needs the
      // Quick Move-Ins tab instead, or Back would land on the wrong list within Available.
      // Model has no tabs (chipSelected already sets null there), so this only applies to
      // Available's own two-tab split.
      return targetChip === LotStatus.Available.toString()
        ? of(dataActions.chipSelected(targetChip), dataActions.tabSelected(TAB_QUICK_MOVE_INS), navigateToAction)
        : of(dataActions.chipSelected(targetChip), navigateToAction);
    }),
  );
}

export default createHousingUnitCardSelectedEpic;
